/**
 * Formatting for the readouts. Every date, time and estimate a screen shows comes from here, so
 * they read the same way everywhere: `21 Sep 2026`, `Monday 21 September 2026`, `4:00`, `about 4 min`.
 */
import { readingSeconds } from '@/shared/library';

/*
 * Month names come from a fixed table rather than `Intl`: Chrome's en-GB data abbreviates September
 * as "Sept", and direction AC reads "Sep" (the handoff's "Completed 9 Sep").
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LONG_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const two = (n: number): string => String(n).padStart(2, '0');

const valid = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** `12 Sep 2026`. Use UTC calendar dates, matching the badge and stored credential record. */
export function formatDate(iso: string | null | undefined): string {
  const d = valid(iso);
  return d ? `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}` : '';
}

/** `Friday 18 September 2026` for the shell's date line and the Home eyebrow, in local time. */
export function longDate(d: Date): string {
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${LONG_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** `12 Sep 2026, 14:05`, in local time. */
export function formatDateTime(iso: string | null | undefined): string {
  const d = valid(iso);
  return d
    ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${two(d.getHours())}:${two(d.getMinutes())}`
    : '';
}

/** `2026-09` -> `Sep 2026` */
export function formatMonth(yyyyMm: string): string {
  const [y, m] = yyyyMm.split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) return yyyyMm;
  return `${MONTHS[m - 1]} ${y}`;
}

/** One decimal, no trailing `.0`; `null` reads as an em-dash-free "n/a" the copy can replace. */
export function formatPct(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '';
  const rounded = Math.round(n * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
}

export function formatInt(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '';
  return new Intl.NumberFormat('en-GB').format(n);
}

/** Media time: `4:00`, `0:45`, `12:00`, or `1:02:05` past an hour. Minutes carry no leading zero. */
export function mmss(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return h > 0 ? `${h}:${two(m)}:${two(r)}` : `${m}:${two(r)}`;
}

/**
 * An estimate: `about 12 min`, from an item's runtime label (the upper bound of a range) or its
 * word count. Estimates always read "about"; only real media reads as `4:00`.
 */
export function aboutMinutes(item: { runtime_label: string; words: number | null }): string {
  return `about ${Math.max(1, Math.round(readingSeconds(item.runtime_label, item.words) / 60))} min`;
}

/** Whole days between two instants, floored at zero. */
export function daysBetween(fromIso: string, toIso: string): number {
  const a = valid(fromIso);
  const b = valid(toIso);
  if (!a || !b) return 0;
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / 86400000));
}

/** The part of an email before the `@`, for a greeting when there is no name. */
export function firstNameOf(name: string | null, email: string): string {
  const first = name?.trim().split(/\s+/)[0];
  if (first) return first;
  return email.split('@')[0] ?? email;
}

export const KIND_LABEL: Record<'brief' | 'module' | 'film' | 'certification', string> = {
  brief: 'Field brief',
  module: 'Module',
  film: 'Film',
  certification: 'Certification',
};

export const TIER_LABEL: Record<'foundation' | 'specialist', string> = {
  foundation: 'Foundation',
  specialist: 'Specialist',
};

/**
 * The human name of a live-data field: the longest of its cards' field labels (written with the
 * course), falling back to the key itself. The key stays on screen as quiet metadata (S-44).
 */
export function slotLabel(slot: { slot_key: string; cards: { field: string }[] }): string {
  const fields = slot.cards.map((card) => card.field.trim()).filter(Boolean);
  return (
    fields.reduce((longest, field) => (field.length > longest.length ? field : longest), '') || slot.slot_key
  );
}
